import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import './TopBar.css'

const SunIcon = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true' className='top-bar__icon'>
    <circle cx='12' cy='12' r='3.5' />
    <path d='M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42' />
  </svg>
)

const MoonIcon = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true' className='top-bar__icon'>
    <path d='M20.5 15.2A8.5 8.5 0 0 1 8.8 3.5 8.5 8.5 0 1 0 20.5 15.2Z' />
  </svg>
)

const UserIcon = () => (
  <svg viewBox='0 0 24 24' aria-hidden='true' className='top-bar__user-icon'>
    <circle cx='12' cy='8' r='3.25' />
    <path d='M5.5 20a6.5 6.5 0 0 1 13 0' />
  </svg>
)

const MenuIcon = ({ open }) => (
  <svg viewBox='0 0 24 24' aria-hidden='true' className='top-bar__icon'>
    {open ? (
      <path d='M6 6l12 12M18 6 6 18' />
    ) : (
      <path d='M4 7h16M4 12h16M4 17h16' />
    )}
  </svg>
)

function TopBar({
  companyName = 'Buck Press Publishing',
  userName = 'Alex Morgan',
  userRole = 'User',
  isMenuOpen = false,
  onMenuToggle,
}) {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return window.localStorage.getItem('northstar-theme') !== 'light'
  })

  useEffect(() => {
    document.documentElement.dataset.theme = isDarkMode ? 'dark' : 'light'
    window.localStorage.setItem(
      'northstar-theme',
      isDarkMode ? 'dark' : 'light',
    )
  }, [isDarkMode])

  useEffect(() => {
    const closeUserMenu = (event) => {
      if (!userMenuRef.current?.contains(event.target)) setIsUserMenuOpen(false)
    }
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsUserMenuOpen(false)
    }
    document.addEventListener('mousedown', closeUserMenu)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('mousedown', closeUserMenu)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [])

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  return (
    <header className='top-bar'>
      <div className='top-bar__left'>
        <button
          className='top-bar__menu-toggle'
          type='button'
          onClick={onMenuToggle}
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMenuOpen}
          aria-controls='workspace-navigation'
          title={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          <MenuIcon open={isMenuOpen} />
        </button>
        <a
          className='top-bar__brand'
          href='/'
          aria-label={`${companyName} home`}
        >
          <span className='top-bar__mark' aria-hidden='true'>
            <img src='/logo1.jpg' alt='' />
          </span>
          <span>{companyName}</span>
        </a>
      </div>

      <div className='top-bar__actions'>
        <button
          className='top-bar__theme-toggle'
          type='button'
          onClick={() => setIsDarkMode((currentMode) => !currentMode)}
          aria-label={
            isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'
          }
          title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? <SunIcon /> : <MoonIcon />}
        </button>
        <div className='top-bar__user-menu' ref={userMenuRef}>
          <button
            className='top-bar__user'
            type='button'
            onClick={() => setIsUserMenuOpen((open) => !open)}
            aria-label='Open user menu'
            aria-expanded={isUserMenuOpen}
            aria-haspopup='menu'
            title='Open user menu'
          >
            <span className='top-bar__avatar' aria-hidden='true'>
              <UserIcon />
            </span>
            <span className='top-bar__user-details'>
              <span className='top-bar__user-name'>{userName}</span>
              <span className='top-bar__user-role'>{userRole}</span>
            </span>
          </button>
          {isUserMenuOpen && (
            <div className='top-bar__dropdown' role='menu'>
              <div className='top-bar__dropdown-user'>
                <strong>{userName}</strong>
                <span>{userRole}</span>
              </div>
              <button
                className='top-bar__logout'
                type='button'
                role='menuitem'
                onClick={handleLogout}
              >
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default TopBar
